import React, { useEffect, useState } from 'react'
import { Avatar, Checkbox } from 'antd'
import CustomModal from 'components/global/CustomModal'
import CustomModalFooter from 'components/global/CustomModalFooter'
import ButtonSpinner from 'components/global/ButtonSpinner'
import { useLeagueStep2 } from 'api/league/LeagueCreation/leagueCreation'
import { useGetOrgOrganizers } from 'api/Organizations'
import { leagueEventStore } from 'state/league/leagueEvent'
import { myStore } from 'state/store'
import { OrgOrganizerParams, OrgOrganizerType } from 'types/organization'
import { getNameSignature } from 'helper/utility'
import { failureMessage } from 'api/global'

interface Props {
  open: boolean
  onClose: () => void
}

const ManageManagersModal: React.FC<Props> = ({ open, onClose }) => {
  const { leagueData, setLeagueData } = leagueEventStore()
  const setModalBtnLoader = myStore(store => store.setModalBtnLoader)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [params] = useState<OrgOrganizerParams>({ status: 'approved' })

  const { data: orgOrganizers, isLoading, refetch } = useGetOrgOrganizers(
    params,
    leagueData?.organizationId,
  )

  const { mutate: updateStep2 } = useLeagueStep2()

  useEffect(() => {
    if (open) {
      setSelectedIds(leagueData?.leagueStaffIds || [])
      refetch()
    }
  }, [open])

  const handleToggle = (organizerId: string) => {
    setSelectedIds(prev =>
      prev.includes(organizerId)
        ? prev.filter(id => id !== organizerId)
        : [...prev, organizerId],
    )
  }

  const handleSave = () => {
    if (!leagueData.website || !leagueData.email || !leagueData.phone) {
      failureMessage('Please complete the league contact information (website, email, phone) before managing league managers.')
      return
    }
    setModalBtnLoader(true)
    updateStep2(
      {
        leagueId: leagueData._id,
        payload: {
          website: leagueData.website,
          email: leagueData.email,
          phone: leagueData.phone,
          leagueStaffIds: selectedIds,
        },
      },
      {
        onSuccess: data => {
          setLeagueData({ ...leagueData, ...data })
          setModalBtnLoader(false)
          onClose()
        },
        onError: () => {
          setModalBtnLoader(false)
        },
      },
    )
  }

  return (
    <CustomModal
      openModal={open}
      isCloseModalCustom
      title="Manage League Managers"
      closeModal={onClose}
      width={560}
      footer={
        <CustomModalFooter
          multibutton
          btnText="Cancel,Save"
          btnAlignment="justify-between"
          closeModal={(() => onClose()) as React.Dispatch<React.SetStateAction<boolean>>}
          onSubmit={handleSave}
        />
      }
    >
      <div className="manage-managers-modal">
        {isLoading ? (
          <div className="flex justify-center py-20px">
            <ButtonSpinner size={24} color="#2ba7ca" />
          </div>
        ) : orgOrganizers && orgOrganizers.length > 0 ? (
          <>
            <p className="text-14 w-400 mb-16px text-gray">
              Select organizers to assign as league managers for this league.
            </p>
            <div className="managers-list">
              {orgOrganizers.map((org: OrgOrganizerType) => {
                const isSelected = selectedIds.includes(org.organizerId)
                return (
                  <div
                    key={org.organizerId}
                    className={`manager-row flex align-center justify-between py-10px px-14px radius-8 mb-8px pointer ${
                      isSelected ? 'manager-row--selected' : ''
                    }`}
                    onClick={() => handleToggle(org.organizerId)}
                  >
                    <div className="flex align-center gap-12px">
                      <Avatar size={40} src={org.organizer.photo?.post}>
                        {getNameSignature(
                          `${org.organizer.firstName} ${org.organizer.lastName || ''}`,
                        )}
                      </Avatar>
                      <div>
                        <p className="text-14 w-500">
                          {org.organizer.firstName} {org.organizer.lastName}
                        </p>
                        <p className="text-12 w-400 text-gray">
                          {org.accessLevel}
                        </p>
                      </div>
                    </div>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggle(org.organizerId)}
                      onClick={e => e.stopPropagation()}
                    />
                  </div>
                )
              })}
            </div>
          </>
        ) : (
          <p className="text-14 w-400 text-gray text-center py-20px">
            No organizers found for this organization.
          </p>
        )}
      </div>
    </CustomModal>
  )
}

export default ManageManagersModal
